import { ActionPermission, LogManager, ModelRow, PermissionsUtil, StringUtil } from "@mcleod/core";
import { Button, TableRow } from "@mcleod/components";
import { CommonContextMenuItems, getDispatchControlBoolean, getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { isSpotOrder } from "./Orders";

export interface MoveData {
    movementId: string;
    orderId: string,
    orderStatus?: string,
    onHold: boolean;
    status: string;
    equipmentId?: string;
    hasRevenueCode: boolean;
    isAssigned: boolean;
    isSpot: boolean;
    isBrokerage: boolean;
    isBrokerageLTL: boolean;
    manifestOnHold?: boolean;
}

interface DispatchButtons {
    btnAssign?: Button;
    btnDispatch?: Button;
    btnManageAssign?: Button;
}

interface AdditionalProps {
    equipmentId?: string;
}

const endpointMap = {
    "lme/powerbroker/brokerage-movements": {
        id: "movement_id",
        orderId: "order_id",
        onHold: "hold",
        status: "move_status",
        revenue_code: "revenue_code",
        isAssigned: "carrier_id",
        isSpot: "subject_order_status",
        isBrokerage: "true",
        isBrokerageLtl: "is_brokerage_ltl",
        manifestOnHold: "manifest_hold"
    },
    "lme/dispatch/planning/csm/customer-service-movements": {
        id: "movement_id",
        orderId: "order_id",
        orderStatus: "order_status",
        onHold: "hold",
        status: "move_status",
        revenue_code: "revenue_code_id",
        isAssigned: "override_payee_id",
        isSpot: "subject_order_status",
        isBrokerage: "brokerage",
        isBrokerageLtl: "is_brokerage_ltl"
    },
    "lme/dispatch/movement": {
        id: "id",
        orderId: "orders.id",
        orderStatus: "orders.status",
        onHold: "orders.on_hold",
        status: "status",
        revenue_code: "orders.revenue_code_id",
        isAssigned: "override_payee_id",
        isSpot: "orders.subject_order_status",
        isBrokerage: "brokerage",
        isBrokerageLtl: "is_brokerage_ltl",
        manifestOnHold: "manifest_hold"
    },
    "lme/powerbroker/carrier-offer": {
        id: "movement_id",
        orderId: "orders.id",
        onHold: "orders.on_hold",
        status: "movement.status",
        isAssigned: "movement.override_payee_id",
        isSpot: "orders.subject_order_status",
        isBrokerage: "movement.brokerage",
        manifestOnHold: "movement.manifest_hold"
    },
    "lme/powerbroker/carrier-offer-list": {
        id: "movement_id",
        onHold: "orders.on_hold",
        status: "movement.status",
        isAssigned: "movement.override_payee_id",
        isSpot: "orders.subject_order_status",
        isBrokerage: "movement.brokerage",
        manifestOnHold: "movement.manifest_hold"
    },
    "lme/powerbroker/available-loads": {
        id: "id",
        orderId: "orders.id",
        onHold: "orders.on_hold",
        status: "status",
        isAssigned: "override_payee_id",
        isSpot: "orders.subject_order_status",
        isBrokerage: "brokerage",
        manifestOnHold: "manifest_hold"
    }
};

const log = LogManager.getLogger("lme.dispatch.DispatchUtil");

export class DispatchUtil {
    /**
     * Returns a MoveData object for use in the isAssignable method.
     * @param row
     */
    public static getMoveData(row: ModelRow): MoveData {
        // get values from row based on datasource type
        const endpointFields = endpointMap[row?._modelPath];
        if (!endpointFields) {
            log.error("Endpoint not supported in DispatchUtil map: " + row?._modelPath);
            return;
        }
        const isBrkPlanning = row._modelPath === "lme/powerbroker/brokerage-movements";
        return {
            movementId: row.get(endpointFields.id),
            orderId: row.get(endpointFields.orderId),
            orderStatus: row.get(endpointFields.orderStatus),
            onHold: row.getBoolean(endpointFields.onHold),
            status: row.get(endpointFields.status),
            hasRevenueCode: !row.isNull(endpointFields.revenue_code),
            isAssigned: !row.isNull(endpointFields.isAssigned),
            isSpot: isSpotOrder(row.get(endpointFields.isSpot)),
            isBrokerage: isBrkPlanning ? true : row.getBoolean(endpointFields.isBrokerage),
            isBrokerageLTL: row.getBoolean(endpointFields.isBrokerageLtl),
            manifestOnHold: row.getBoolean(endpointFields.manifestOnHold),
        }
    }

    /**
     * Checks if the movement can be assigned to a carrier.  If the assign and/or dispatch buttons are passed in,
     * they will be enabled/disabled based on the results of the checks and a disabled reason will be set.
     * Returns a boolean if assignment should be allowed in case the caller needs to handle things differently.
     * @param row
     * @param dispatchButtons
     * @param addlProps
     */
    public static isAssignable(row: ModelRow, dispatchButtons: DispatchButtons, addlProps: AdditionalProps = {}): boolean {
        const moveData = this.getMoveData(row);
        const { btnAssign, btnDispatch, btnManageAssign } = dispatchButtons;
        const { equipmentId } = addlProps;
        const status = moveData.status;

        if (btnAssign != null) btnAssign.disabledTooltip = null;
        if (btnManageAssign != null) btnManageAssign.disabledTooltip = null;
        if (btnDispatch != null) btnDispatch.disabledTooltip = null;

        if (!!moveData == false) {
            const msg = "{0} button is disabled because the movement could not be found.";
            if (btnManageAssign != null) this.updateButton(btnManageAssign, false, StringUtil.stringFormat(msg, "Manage Assignments"));
            if (btnAssign != null) this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            if (btnDispatch != null) this.updateButton(btnDispatch, false, StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check for hold status
        if (moveData.onHold && (status === "A" || (!moveData.isBrokerage && status == "P")) && LMEGeneralSettings.getSingleton().getDispatchControl().prevent_dsp_hold === "Y") {
            const msg = "{0} button is disabled because the order is on hold.";
            if (btnManageAssign != null) this.updateButton(btnManageAssign, false, StringUtil.stringFormat(msg, "Manage Assignments"));
            if (btnAssign != null) this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            if (btnDispatch != null) this.updateButton(btnDispatch, moveData.isBrokerage, (moveData.isBrokerage) ? null : StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check for manifest hold status
        if (moveData.manifestOnHold && (status === "A" || (!moveData.isBrokerage && status == "P"))) {
            const msg = "{0} button is disabled because the manifest is on hold.";
            if (btnManageAssign != null) this.updateButton(btnManageAssign, false, StringUtil.stringFormat(msg, "Manage Assignments"));
            if (btnAssign != null) this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            if (btnDispatch != null) this.updateButton(btnDispatch, moveData.isBrokerage, (moveData.isBrokerage) ? null : StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check for spot order
        if (!moveData.isAssigned && moveData.isSpot) {
            const msg = "{0} button is disabled because the order is a spot order.";
            if (btnManageAssign != null) this.updateButton(btnManageAssign, false, StringUtil.stringFormat(msg, "Manage Assignments"));
            if (btnAssign != null) this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            if (btnDispatch != null) this.updateButton(btnDispatch, false, StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check if revenue code is enforced and if the order has one
        if (getDispatchControlBoolean("enforce_revenu_cod") && !moveData.hasRevenueCode) {
            const msg = "Revenue codes are enforced and required{0}.";
            if (btnManageAssign != null) this.updateButton(btnManageAssign, false, StringUtil.stringFormat(msg, " for preassignment"));
            if (btnAssign != null) this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, " for assignment"));
            if (btnDispatch != null) this.updateButton(btnDispatch, false, StringUtil.stringFormat(msg, " to dispatch"));
            return false;
        }

        if (!moveData.isBrokerage) {
            const msg = "Carriers may not be {0} to a movement with a movement type of asset.";
            if (btnAssign != null)
                this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "assigned"));
            if (equipmentId == null) {
                const dispatchMsg = `${getDispatchControlValue("dispatch_by") === "T" ? "Tractor" : "Driver"} must be assigned to dispatch.`;
                this.updateButton(btnDispatch, false, dispatchMsg);
            } else if ("D" === moveData.status) {
                const dispatchMsg = "A delivered movement cannot be {0}.";
                if (btnManageAssign != null)
                    this.updateButton(btnManageAssign, false, StringUtil.stringFormat(dispatchMsg, "assigned"));
            }
        } else if (moveData.isAssigned) {
            // check if carrier is already assigned
            if (btnAssign != null)
                this.updateButton(btnAssign, false, "Assign Carrier button is disabled because a carrier has already been assigned.");
            if (btnDispatch != null)
                this.updateButton(btnDispatch, true)
            return false;
        }

        // assignment & dispatch allowed
        if (btnAssign != null && btnAssign.disabledTooltip == null)
            this.updateButton(btnAssign, true);
        if (btnManageAssign != null && btnManageAssign.disabledTooltip == null)
            this.updateButton(btnManageAssign, true);
        if (btnDispatch != null && btnDispatch.disabledTooltip == null)
            this.updateButton(btnDispatch, true);
        return true;
    }

    public static canDispatch(row: ModelRow, btnDispatch: Button, addlProps: AdditionalProps = {}): boolean {
        const moveData = this.getMoveData(row);
        const { equipmentId } = addlProps;
        const status = moveData.status;
        if (!!moveData == false) {
            const msg = "{0} button is disabled because the movement could not be found.";
            if (btnDispatch != null) this.updateButton(btnDispatch, false, StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        if (moveData.orderId == null) {
            const msg = "{0} button is disabled because the order could not be found.";
            if (btnDispatch != null) this.updateButton(btnDispatch, false, StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check for hold status
        if (moveData.onHold && (status === "A" || (!moveData.isBrokerage && status == "P")) && LMEGeneralSettings.getSingleton().getDispatchControl().prevent_dsp_hold === "Y") {
            const msg = "{0} button is disabled because the order is on hold.";
            if (btnDispatch != null) this.updateButton(btnDispatch, moveData.isBrokerage, (moveData.isBrokerage) ? null : StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check for spot order
        if (!moveData.isAssigned && moveData.isSpot) {
            const msg = "{0} button is disabled because the order is a spot order.";
            if (btnDispatch != null) this.updateButton(btnDispatch, false, StringUtil.stringFormat(msg, "Dispatch"));
            return false;
        }

        // check if movement is asset
        if (!moveData.isBrokerage) {
            const msg = "Carriers may not be {0} to a movement with a movement type of asset.";
            const dispatchMsg = `${getDispatchControlValue("dispatch_by") === "T" ? "Tractor" : "Driver"} must be assigned to dispatch.`;
            if (btnDispatch != null) this.updateButton(btnDispatch, equipmentId != null, dispatchMsg);
            return btnDispatch.enabled;
        }

        //  dispatch allowed
        if (btnDispatch != null) this.updateButton(btnDispatch, true);
        return true;
    }

    public static updateButton(button: Button, status: boolean, message?: string) {
        button.enabled = status;
        button.disabledTooltip = message;
    }


    /**
     * Checks if multiple movements can be assigned to a carrier.  When the assign button is passed in,
     * it will be enabled/disabled based on the results of the checks and a disabled reason will be set.
     * Returns a boolean if assignment should be allowed in case the caller needs to handle things differently.
     * @param rows
     * @param dispatchButtons
     * @param addlProps
     */
    public static areMultiAssignable(rows: TableRow[], btnAssign: Button): boolean {
        let msg = "";
        if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_MULTISELECT_ASSIGN_CARRIER)) {
            msg = "Only one carrier can be assigned at a time"
            this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            return false
        }
        if (rows.length > getDispatchControlValue("max_assign_selections")) {
            msg = CommonContextMenuItems.MAX_ROWS
            this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            return false
        }
        for (const tableRow of rows) {
            const row = tableRow.data as ModelRow;
            const moveData = this.getMoveData(row);
            const status = moveData.status;
            if (!!moveData == false) {
                msg += "{0} button is disabled because the order #" + row.get("order_id") + " could not be found.\n";
            }
            // check for hold status
            if (moveData.onHold && (status === "A" || (!moveData.isBrokerage && status == "P")) && LMEGeneralSettings.getSingleton().getDispatchControl().prevent_dsp_hold === "Y") {
                msg += "{0} button is disabled because the order #" + row.get("order_id") + " is on hold.\n";
            }
            // check for spot order
            if (!moveData.isAssigned && moveData.isSpot) {
                msg += "{0} button is disabled because the order #" + row.get("order_id") + " is a spot order.\n";
            }
            // check if carrier is already assigned
            if (moveData.isAssigned) {
                msg += "Assign Carrier button is disabled because a carrier has already been assigned to order #" + row.get("order_id") + ".\n";
            }
        }
        if (msg !== "") {
            this.updateButton(btnAssign, false, StringUtil.stringFormat(msg, "Assign Carrier"));
            return false
        }
        this.updateButton(btnAssign, true);
        return true;
    }
}
